import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Box,
  Typography,
} from "@mui/material";
import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { format } from "date-fns";
import { Ride, RideStatus, RideType } from "../../../types/ride";
import { getCurrencySymbol } from "../../../utils/util";

interface PartnerRideListProps {
  rides: Ride[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
  onPageChange: (page: number) => void;
  onRideClick: (rideId: string) => void;
}

const getStatusColor = (status: RideStatus) => {
  const colors: Record<
    RideStatus,
    "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"
  > = {
    [RideStatus.PENDING]: "warning",
    [RideStatus.ACCEPTED]: "info",
    [RideStatus.STARTED]: "primary",
    [RideStatus.ARRIVED]: "info",
    [RideStatus.ENDED]: "success",
    [RideStatus.CANCELED]: "error",
    [RideStatus.PAYMENT_SUCCEED]: "success",
    [RideStatus.PAYMENT_FAILED]: "error",
    [RideStatus.CANCELED_WITH_PAYMENT]: "success",
  };
  return colors[status];
};

const getRideTypeLabel = (type: RideType) => {
  const labels: Record<RideType, string> = {
    [RideType.NORMAL]: "Normal",
    [RideType.SHARED]: "Shared",
    [RideType.DELIVERY]: "Delivery",
  };
  return labels[type];
};

export default function PartnerRideList({ rides, pagination, onPageChange, onRideClick }: PartnerRideListProps) {
  const handleChangePage = (event: unknown, newPage: number) => {
    onPageChange(newPage + 1); // MUI uses 0-based pagination, our API uses 1-based
  };

  if (rides.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="text.secondary">No rides found</Typography>
      </Box>
    );
  }

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Driver</TableCell>
              <TableCell>Car</TableCell>
              <TableCell>Pickup</TableCell>
              <TableCell>Dropoff</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Fare</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rides.map((ride) => {
              // Debug: Log partner commission for each ride
              if (rides.indexOf(ride) === 0) {
                console.log("Sample ride data:", {
                  _id: ride._id,
                  partner_commission: ride.partner_commission,
                  fare_amount: ride.fare_amount,
                  has_partner_commission: typeof ride.partner_commission === "number",
                  partner_commission_value: ride.partner_commission,
                });
              }
              return (
              <TableRow key={ride._id} hover>
                <TableCell>
                  {ride.driver ? (
                    <Box>
                      <Typography variant="body2">{ride.driver.driver_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ride.driver.driver_phone_number}
                      </Typography>
                    </Box>
                  ) : (
                    "Not assigned"
                  )}
                </TableCell>
                <TableCell>
                  {ride.car ? (
                    <Box>
                      <Typography variant="body2">
                        {ride.car.make} {ride.car.model}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ride.car.car_registration_number}
                      </Typography>
                      {ride.car.car_type && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {ride.car.car_type}
                        </Typography>
                      )}
                      {(ride.car as any).owner_name && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          Owner: {(ride.car as any).owner_name}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No car assigned
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {ride.pickup_location.place}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {ride.destination_location.place}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={ride.ride_status}
                    size="small"
                    color={getStatusColor(ride.ride_status)}
                    sx={
                      ride.ride_status === RideStatus.CANCELED_WITH_PAYMENT
                        ? { backgroundColor: "#CBE256", color: "#222" }
                        : {}
                    }
                  />
                  {ride.cancelled_by &&
                    (ride.ride_status === RideStatus.CANCELED ||
                      ride.ride_status === RideStatus.CANCELED_WITH_PAYMENT) && (
                      <Box mt={1}>
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          color="text.primary"
                          display="inline"
                        >
                          Cancelled by:
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="inline"
                          sx={{ ml: 0.5 }}
                        >
                          {ride.cancelled_by.toLowerCase()}
                        </Typography>
                        {ride.cancellation_reason && (
                          <>
                            <br />
                            <Typography
                              variant="subtitle2"
                              fontWeight="bold"
                              color="text.primary"
                              display="inline"
                            >
                              Cancellation Reason:
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="inline"
                              sx={{ ml: 0.5 }}
                            >
                              {ride.cancellation_reason}
                            </Typography>
                          </>
                        )}
                      </Box>
                    )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {Math.round(ride.fare_amount).toLocaleString()}{" "}
                    {getCurrencySymbol(ride.country_code)}
                  </Typography>
                  {ride.payment_mode && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {ride.payment_mode}
                    </Typography>
                  )}
                  {typeof ride.driver_payable_amount === "number" &&
                    ride.driver_payable_amount > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Driver: {Math.round(ride.driver_payable_amount).toLocaleString()}{" "}
                        {getCurrencySymbol(ride.country_code)}
                      </Typography>
                    )}
                  {typeof ride.teracar_commission === "number" && ride.teracar_commission > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      TeraCar: {Math.round(ride.teracar_commission).toLocaleString()}{" "}
                      {getCurrencySymbol(ride.country_code)}
                    </Typography>
                  )}
                  {(() => {
                    const partnerCommission = ride.partner_commission;
                    
                    // Handle different data types
                    let commissionValue: number | null = null;
                    if (typeof partnerCommission === "number") {
                      commissionValue = partnerCommission;
                    } else if (typeof partnerCommission === "string") {
                      const parsed = parseFloat(partnerCommission);
                      commissionValue = isNaN(parsed) ? null : parsed;
                    } else if (partnerCommission !== null && partnerCommission !== undefined) {
                      // Try to convert to number
                      commissionValue = Number(partnerCommission);
                      if (isNaN(commissionValue)) {
                        commissionValue = null;
                      }
                    }
                    
                    // Show partner commission if it exists
                    if (commissionValue !== null && !isNaN(commissionValue)) {
                      return (
                        <Typography 
                          variant="caption" 
                          color={commissionValue > 0 ? "primary" : "text.secondary"}
                          fontWeight={commissionValue > 0 ? "bold" : "normal"}
                          display="block"
                        >
                          Partner: {Math.round(commissionValue).toLocaleString()}{" "}
                          {getCurrencySymbol(ride.country_code)}
                        </Typography>
                      );
                    }
                    
                    return null;
                  })()}
                </TableCell>
                <TableCell>{format(new Date(ride.created_at), "MMM d, yyyy HH:mm")}</TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => onRideClick(ride._id)}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={pagination.total}
        page={pagination.page - 1} // Convert to 0-based for MUI
        rowsPerPage={pagination.limit}
        rowsPerPageOptions={[pagination.limit]} // We only support one page size for now
        onPageChange={handleChangePage}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
      />
    </Paper>
  );
}

